import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Button as AntButton, notification } from 'antd';
import { Spinner } from 'Components';
import { useMutation } from '@apollo/client';
import ObjectivesAdminCreationErrorsModal from './ObjectivesAdminCreationErrorsModal';
import { TUPLA_CSV_OBJETIVO_BATCH, INICIAR_PROCESADO_OBJETIVOS } from 'Queries';

/* Contenido de la parte superior
  DERECHA:
  - Botón para descargar la plantilla del CSV
  IZQUIERDA
  - Botón para escoger un único archivo del PC
  - Texto con el nombre del fichero
  - Botón para subir el fichero al sistema
*/
const ObjectivesAdminCreationHeader = ({ fetchData, currentPage, pageSize }) => {
  // TEXTOS DE ERROR
  const ERROR_MESSAGES = {
    DATE_MUST_BE_AFTER_OR_EQUAL: (row, column, otherColumn) => `Fila ${row}, columna ${column}: La fecha asignada debe ser posterior o igual a la fecha en columna ${otherColumn}.`,
    DATE_MUST_BE_BEFORE_OR_EQUAL: (row, column, otherColumn) => `Fila ${row}, columna ${column}: La fecha asignada debe ser anterior o igual a la fecha en columna ${otherColumn}.`,
    DATE_YEAR_MUST_BE_CURRENT_OR_FUTURE: (row, column) => `Fila ${row}, columna ${column}: El año de la fecha asignada debe ser el actual o uno posterior.`,
    EMPTY_FILE: () => `Error de fichero: Se ha enviado un fichero sin valores para procesar.`,
    EMPTY_VALUE: (row, column) => `Fila ${row}, columna ${column}: Valor vacío.`,
    EMPTY_ROW: (row) => `Fila ${row}: Vacía.`,
    INVALID_DATE_FORMAT: (row, column) => `Fila ${row}, columna ${column}: Se esperaba valor con formato día/mes/año.`,
    INVALID_DATE_NUMBER: (row, column, value) => `Fila ${row}, columna ${column}: No existe la fecha ${value} en el calendario.`,
    INVALID_FILE_FORMAT: () => `Error de fichero: El fichero está vacío, presenta un número distinto de columnas en la cabecera o no usa el separador ';'. No se leerá su contenido.`,
    INVALID_HEADER_FORMAT: () => `Error de fichero: El contenido de las cabeceras del fichero CSV no coincide con las de la plantilla. No se leerá su contenido.`,
    INVALID_FORMAT: (row, column) => `Fila ${row}, columna ${column}: Se esperaba un valor vacío, o un valor o valores entre corchetes y separados por comas.`,
    NEGATIVE_VALUE: (row, column) => `Fila ${row}, columna ${column}: Valor negativo.`,
    NON_EXPECTED_VALUE: (row, column, expectedValues) => `Fila ${row}, columna ${column}: Se esperaba uno de los siguientes valores: ${expectedValues.join(', ')}.`,
    NON_INF_VALUE: (row, column) => `Fila ${row}, columna ${column}: Nombre de objetivo no comienza por "INF".`,
    NON_NUMERIC_VALUE: (row, column) => `Fila ${row}, columna ${column}: Se esperaba un valor numérico.`,
    NON_EMPTY_VALUE: (row, column) => `Fila ${row}, columna ${column}: Se esperaba valor vacío.`,
    NON_CSV_FILE: () => `Error de fichero: Se requiere fichero CSV. No se leerá su contenido.`,
    SUM_MUST_BE_EQUAL: (row, firstColumn, last_column, otherColumn) => `Error en fila ${row}: La suma de los valores de las columnas ${firstColumn} hasta la ${last_column} debe ser igual al valor de la columna ${otherColumn}.`,
    VALUES_UNCHECKED: (row, firstColumn, last_column) => `Error en fila ${row}: Los valores de las columnas ${firstColumn} hasta la ${last_column} no se han revisado porque hubo un problema con el periodo marcado o con la cantidad objetivo.`,
  }

  // TEXTO POR DEFECTO
  const DEFAULT_TEXT = 'Ningún archivo seleccionado';

  // CABECERAS
  const csvHeaders = [ // 42 cabeceras
    'CODIGO DISTRIBUIDOR', // Columna A
    'OBJETIVO', // Columna B
    'POR EQUIPO', // Columna C
    'POR MES', // Columna D
    'TIPO OBJETIVO', // Columna E
    'FECHA INICIO', // Columna F
    'FECHA FIN', // Columna G
    'INCENTIVO COMERCIAL', // Columna  H
    'INCENTIVO JE', // Columna I
    'INCENTIVO JV', // Columna J
    'CANTIDAD OBJETIVO', // Columna K
    'MES1', // Columna L
    'MES2', // Columna M
    'MES3', // Columna N
    'MES4', // Columna O
    'MES5', // Columna P
    'MES6', // Columna Q
    'MES7', // Columna R
    'MES8', // Columna S
    'MES9', // Columna T
    'MES10', // Columna U
    'MES11', // Columna V
    'MES12', // Columna W
    'ART FAMILIA', // Columna X
    'ART AF', // Columna Y
    'ART MARCA', // Columna Z
    'ART AM', // Columna AA
    'ART AS', // Columna AB
    'ART SEGM1', // Columna AC
    'ART SEGM2', // Columna AD
    'ART SEGM3', // Columna AE
    'ART SEGM4', // Columna AF
    'CLIENTE CP', // Columna AG
    'CLIENTE POBLACION', // Columna AH
    'CLIENTE RP', // Columna AI
    'CLIENTE SD1', // Columna AJ
    'CLIENTE SD2', // Columna AK
    'CLIENTE SD3', // Columna AL
    'CLIENTE SH1', // Columna AM
    'CLIENTE SH2', // Columna AN
    'CLIENTE SH3', // Columna AO
    'CLIENTE SH4' // Columna AR
  ];

  // VARIABLES JSON
  const jsonVariableNames = [
    'codigo_distribuidor', // Columna A
    'objetivo', // Columna B
    'por_equipo', // Columna C
    'por_mes', // Columna D
    'tipo_objetivo', // Columna E
    'fecha_inicio', // Columna F
    'fecha_fin', // Columna G
    'incentivo_comercial', // Columna  H
    'incentivo_je', // Columna I
    'incentivo_jv', // Columna J
    'cantidad_objetivo', // Columna K
    'mes1', // Columna L
    'mes2', // Columna M
    'mes3', // Columna N
    'mes4', // Columna O
    'mes5', // Columna P
    'mes6', // Columna Q
    'mes7', // Columna R
    'mes8', // Columna S
    'mes9', // Columna T
    'mes10', // Columna U
    'mes11', // Columna V
    'mes12', // Columna W
    'art_familia', // Columna X
    'art_af', // Columna Y
    'art_marca', // Columna Z
    'art_am', // Columna AA
    'art_as', // Columna AB
    'art_segm1', // Columna AC
    'art_segm2', // Columna AD
    'art_segm3', // Columna AE
    'art_segm4', // Columna AF
    'cliente_cp', // Columna AG
    'cliente_poblacion', // Columna AH
    'cliente_rp', // Columna AI
    'cliente_sd1', // Columna AJ
    'cliente_sd2', // Columna AK
    'cliente_sd3', // Columna AL
    'cliente_sh1', // Columna AM
    'cliente_sh2', // Columna AN
    'cliente_sh3', // Columna AO
    'cliente_sh4' // Columna AR
  ];

  // VALORES ESPERADOS
  const OBJECTIVES_VALUES = ['NUMERICA', 'VOLUMEN'];
  const YES_NO_VALUES = ['SI', 'NO'];

  // EXPRESIONES REGULARES
  const dateRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/; // Exp. regular para las fechas
  const filterRegex = /^$|(\[([^\[\],]+(?:,[^\[\],]+)*)\])$/; // Exp. regular para los textos entre corchetes
  const infRegex = /^INF/; // Exp. regular para verificar que el texto comienza por 'INF'
  const mesRegex = /^mes\d+$/;

  // DATOS DE FICHERO
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState(DEFAULT_TEXT);
  const fileInputRef = useRef(null);

  // MODAL DE ERRORES
  const modalRef = useRef();

  // VARIABLES AUXILIARES
  const [isLoading, setIsLoading] = useState(false);

  // LLAMADA AL BACK
  const [agregarTuplaCsvObjBatch, { loading, error, data }] = useMutation(TUPLA_CSV_OBJETIVO_BATCH);
  const [iniciarProcesadoObjetivos] = useMutation(INICIAR_PROCESADO_OBJETIVOS);

  // FUNCIONES
  useEffect(() => {
    setFileName(DEFAULT_TEXT);
    setFile(null);
  }, []);

  const downloadEmptyCSV = () => {
    const csvContent = `${csvHeaders.join(';')}\n`; // Crea el contenido del CSV con las cabeceras
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFileName(selectedFile.name);
      setFile(selectedFile);
    }
  };

  /// Función que controla la subida del fichero
  const handleFileUpload = () => {
    setIsLoading(true);

    if (fileName && _getExtension(fileName) !== 'csv') {
      const errors = [ERROR_MESSAGES.NON_CSV_FILE()];
      // Lanzamos el error
      _throwErrors(errors, {});
    }
    else if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const csvContent = event.target.result;
        const lines = csvContent.split('\r\n');
        // Comprobamos los errores
        const errors = _checkFileFormatAndHeaders(lines); // Primero la cabecera
        let json = {}

        if (errors.length === 0) { // Si no hubo errores, procesamos el fichero
          const result = _proccessFile(lines);
          errors.push(...result.errors);
          json = result.json;
        }
        // Soltamos los errores (a su vez esto gestiona el envío al back)
        _throwErrors(errors, json);
      };
      reader.readAsText(file, 'ISO-8859-1');
    }
  };


  const _sendJson = async (json) => {
    if (!json || Object.keys(json).length === 0) {
      notification.warning({
        message: 'Aviso',
        description: 'No se mandan datos al servidor.',
        placement: 'topRight',
        duration: 5,
      })
      _resetFile();
      return;
    }
    try {
      const result = await agregarTuplaCsvObjBatch({
        variables: {
          tuplaObjetivo: json
        }
      });
      const message = result.data.agregarTuplaCsvObjBatch;
      if (message === "ERROR: El procesado de datos se está ejecutando.") {
        notification.error({
          message: 'Error enviando datos',
          description: 'El procesado de datos se está ejecutando. Por favor, espere hasta que termine para enviar información.',
          placement: 'topRight',
          duration: 5,
        })
      } else {
        notification.success({
          message: 'Datos enviados',
          description: json.length > 1 ? `Se han enviado ${json.length} registros al servidor.` : ' Se ha enviado 1 registro al servidor.',
          placement: 'topRight',
          duration: 5,
        })
        // Si fue exitoso, se inicia procesado
        try {
          await iniciarProcesadoObjetivos();
        } catch (err) {
          console.error("Error iniciando procesado:", err);
        }
      }
    } catch (err) {
      console.error("Error enviando datos:", err);
    } finally {
      _resetFile();
      fetchData(currentPage, pageSize);
    }
  };

  /// Función para comprobar el contenido del fichero
  const _checkContent = (lines) => {
    const errors = [];
    const goodLines = [];
    for (let i = 1; i < lines.length - 1; i++) {
      const line = lines[i].trim();
      if (line === '') {
        if (i < lines.length - 1 && lines[i + 1].trim() !== '') {
          errors.push(ERROR_MESSAGES.EMPTY_ROW);
        }
        continue;
      }
      const values = line.split(';');
      const sumErrors = errors.length;
      // COLUMNA A
      errors.push(..._checkNumericValue(values[0].trim(), i + 1, 'A'));
      // COLUMNA B
      errors.push(..._checkObjectiveName(values[1].trim(), i + 1, 'B'));
      // COLUMNA C
      errors.push(..._checkExpectedValues(values[2].trim(), i + 1, 'C', YES_NO_VALUES));
      // COLUMNA D
      errors.push(..._checkExpectedValues(values[3].trim(), i + 1, 'D', YES_NO_VALUES));
      // COLUMNA E
      errors.push(..._checkExpectedValues(values[4].trim(), i + 1, 'E', OBJECTIVES_VALUES));
      //COLUMNA F
      const firstDateResult = _checkDateValue(values[5].trim(), i + 1, 'F', 'G', values[6].trim(), true);
      errors.push(...firstDateResult.errors);
      //COLUMNA G
      const secondDateResult = _checkDateValue(values[6].trim(), i + 1, 'G', 'F', values[5].trim(), false);
      errors.push(...secondDateResult.errors);
      // COLUMNA H
      errors.push(..._checkNumericValue(values[7].trim(), i + 1, 'H'));
      // COLUMNA I
      errors.push(..._checkNumericValue(values[8].trim(), i + 1, 'I'));
      // COLUMNA J
      errors.push(..._checkNumericValue(values[9].trim(), i + 1, 'J'));
      // COLUMNAS K (para ver si el valor es correcto) y luego L - W
      const arrayCols = ['K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W']; // Array con los valores de las columnas K a la W
      errors.push(..._checkMonthsValues(values.slice(10, 23).map(value => value.trim()), i + 1, arrayCols, firstDateResult.month, secondDateResult.month));
      // COLUMNA X - AP
      // Array con los valores de las columnas X a la AP
      const arrayCols2 = ['X', 'Y', 'Z', 'AA', 'AB', 'AC', 'AD', 'AE', 'AF', 'AG', 'AH', 'AI', 'AJ', 'AK', 'AL', 'AM', 'AN', 'AO', 'AP'];
      const finalSubArray = values.slice(23).map(value => value.trim());
      for (let j = 0; j < finalSubArray.length; j++) {
        errors.push(..._checkFilterFormat(finalSubArray[j], i + 1, arrayCols2[j]));
      }
      if (sumErrors === errors.length) { // Si no se han incrementado los errores
        goodLines.push(line);
      }
    }
    return { errors: errors, goodLines: goodLines };
  }

  // Función para formatear fechas
  const _formatDate = (dateString) => {
    if (!dateString) return dateString; // Si está vacío, no hacer nada
    const dateParts = dateString.split('/');
    const day = dateParts[0].padStart(2, '0');
    const month = dateParts[1].padStart(2, '0');
    const year = dateParts[2];
    return `${day}/${month}/${year}`;
  }

  // Función para validar formatos de ficheros de entrada
  const _checkFilterFormat = (value, row, column) => {
    const errors = [];
    if (value !== '' && !filterRegex.test(value)) { // Si el valor no está vacío y no tiene el formato adecuado
      errors.push(ERROR_MESSAGES.INVALID_FORMAT(row, column));
    }
    return errors;
  }

  // Función para comprobar los valores de cada mes
  const _checkMonthsValues = (values, row, arrayColumns, firstMonth, lastMonth) => {
    const errors = [];
    errors.push(..._checkNumericValue(values[0], row, arrayColumns[0])); // Se comprueba valor final (cantidad objetivo)
    if (errors.length > 0 || firstMonth === 0 || lastMonth === 0 || firstMonth > lastMonth) { // Si hubo un problema con las fechas o con la cantidad objetivo
      errors.push(ERROR_MESSAGES.VALUES_UNCHECKED(row, arrayColumns[1], arrayColumns[arrayColumns.length - 1]));
    } else {
      let sum = 0;
      for (let i = 1; i < values.length; i++) {
        if (i >= firstMonth && i <= lastMonth) { // Si se encuentra dentro del rango de meses
          let initialsErrors = errors.length;
          errors.push(..._checkNumericValue(values[i], row, arrayColumns[i])); // Comprobamos si tiene formato numérico
          if (initialsErrors === errors.length && sum !== -1) { // Si no hubo problemas
            sum += parseFloat(values[i]); // se suma al total para comparar
          } else if (sum !== -1) {
            sum = -1;
          }
        } else if (values[i] !== '') { // Si no, y es distinto de vacío
          errors.push(ERROR_MESSAGES.NON_EMPTY_VALUE(row, arrayColumns[i]));
          sum = -1;
        }
      }
      if (sum !== -1 && sum !== parseFloat(values[0])) { // Si no hubo ningún problema previo y la suma da distinta al valor objetivo total
        errors.push(ERROR_MESSAGES.SUM_MUST_BE_EQUAL(row, arrayColumns[1], arrayColumns[arrayColumns.length - 1], arrayColumns[0]));
      }
    }
    return errors;
  }

  // Función para comprobar que una fecha es válida
  const _isValidDate = (year, month, day) => {
    const date = new Date(year, month, day);
    return date.getDate() === day && date.getMonth() === month && date.getFullYear() === year;
  }

  // Comprobación de valor de fecha
  const _checkDateValue = (value, row, column, otherColumn, dateToCompare, isDateBefore) => {
    const errors = [];
    let month = 0; // Si queda en 0 es que hubo algún error
    if (value === '') { // Valor vacío
      errors.push(ERROR_MESSAGES.EMPTY_VALUE(row, column));
    } else if (!dateRegex.test(value)) { // Formato de fecha no válido
      errors.push(ERROR_MESSAGES.INVALID_DATE_FORMAT(row, column));
    } else {
      const dateParts = value.split('/');
      const year = parseInt(dateParts[2], 10);
      const month2 = parseInt(dateParts[1], 10) - 1;
      const day = parseInt(dateParts[0], 10)
      const currentYear = new Date().getFullYear();
      if (!_isValidDate(year, month2, day)) {
        errors.push(ERROR_MESSAGES.INVALID_DATE_NUMBER(row, column, value));
      } else if (year < currentYear) { // Año anterior al actual
        errors.push(ERROR_MESSAGES.DATE_YEAR_MUST_BE_CURRENT_OR_FUTURE(row, column));
      } else if (dateToCompare !== '' && dateRegex.test(value)) {
        const date = new Date(year, month2, day);
        const dateToCompareDate = new Date(parseInt(dateToCompare.split('/')[2], 10), parseInt(dateToCompare.split('/')[1], 10) - 1, parseInt(dateToCompare.split('/')[0], 10));
        if (isDateBefore && date > dateToCompareDate) { // Fecha posterior en vez de anterior o igual
          errors.push(ERROR_MESSAGES.DATE_MUST_BE_BEFORE_OR_EQUAL(row, column, otherColumn));
        } else if (!isDateBefore && date < dateToCompareDate) { // Fecha anterior en vez de posterior o igual
          errors.push(ERROR_MESSAGES.DATE_MUST_BE_AFTER_OR_EQUAL(row, column, otherColumn));
        } else { // Si no hubo problemas
          month = parseInt(dateParts[1]);
        }
      }
    }
    return { errors: errors, month: month };
  }

  /// Comprobación para valores numéricos
  const _checkNumericValue = (value, row, column) => {
    const errors = [];
    if (value === '') { // Valor vacío
      errors.push(ERROR_MESSAGES.EMPTY_VALUE(row, column));
    } else if (isNaN(value)) { // Valor no numérico
      errors.push(ERROR_MESSAGES.NON_NUMERIC_VALUE(row, column));
    } else if (value < 0) { // Valor negativo
      errors.push(ERROR_MESSAGES.NEGATIVE_VALUE(row, column));
    }
    return errors;
  }

  // Comprobación del nombre de objetivos
  const _checkObjectiveName = (value, row, column) => {
    const errors = [];
    if (value === '') { // Valor vacío
      errors.push(ERROR_MESSAGES.EMPTY_VALUE(row, column));
    } else if (!infRegex.test(value)) { // Valor no empieza por 'INF'
      errors.push(ERROR_MESSAGES.NON_INF_VALUE(row, column));
    }
    return errors;
  }

  // Comprobación de valores esperados
  const _checkExpectedValues = (value, row, column, expectedValues) => {
    const errors = [];
    if (value === '') { // Valor vacío
      errors.push(ERROR_MESSAGES.EMPTY_VALUE(row, column));
    } else if (!expectedValues.includes(value)) { // Valor no válido
      errors.push(ERROR_MESSAGES.NON_EXPECTED_VALUE(row, column, expectedValues));
    }
    return errors;
  }

  /// Función para comprobar las cabeceras del 
  const _checkFileFormatAndHeaders = (lines) => {
    const errors = [];
    if (lines.length === 0 || (lines.length > 0 && lines[0].trim().split(';').length !== 42)) {
      errors.push(ERROR_MESSAGES.INVALID_FILE_FORMAT());
    } else {
      const header = lines[0].trim().split(';');
      if (
        header[0] !== csvHeaders[0] || header[1] !== csvHeaders[1] || header[2] !== csvHeaders[2] ||
        header[3] !== csvHeaders[3] || header[4] !== csvHeaders[4] || header[5] !== csvHeaders[5] ||
        header[6] !== csvHeaders[6] || header[7] !== csvHeaders[7] || header[8] !== csvHeaders[8] ||
        header[9] !== csvHeaders[9] || header[10] !== csvHeaders[10] || header[11] !== csvHeaders[11] ||
        header[12] !== csvHeaders[12] || header[13] !== csvHeaders[13] || header[14] !== csvHeaders[14] ||
        header[15] !== csvHeaders[15] || header[16] !== csvHeaders[16] || header[17] !== csvHeaders[17] ||
        header[18] !== csvHeaders[18] || header[19] !== csvHeaders[19] || header[20] !== csvHeaders[20] ||
        header[21] !== csvHeaders[21] || header[22] !== csvHeaders[22] || header[23] !== csvHeaders[23] ||
        header[24] !== csvHeaders[24] || header[25] !== csvHeaders[25] || header[26] !== csvHeaders[26] ||
        header[27] !== csvHeaders[27] || header[28] !== csvHeaders[28] || header[29] !== csvHeaders[29] ||
        header[30] !== csvHeaders[30] || header[31] !== csvHeaders[31] || header[32] !== csvHeaders[32] ||
        header[33] !== csvHeaders[33] || header[34] !== csvHeaders[34] || header[35] !== csvHeaders[35] ||
        header[36] !== csvHeaders[36] || header[37] !== csvHeaders[37] || header[38] !== csvHeaders[38] ||
        header[39] !== csvHeaders[39] || header[40] !== csvHeaders[40] || header[41] !== csvHeaders[41]
      ) {
        errors.push(ERROR_MESSAGES.INVALID_HEADER_FORMAT());
      }
    }
    return errors
  }

  /// Función con la lógica para mostrar los errores
  const _throwErrors = (errors, json) => {
    // Mostramos los errores en el modal
    modalRef.current.showErrors(errors, json);
  }

  /// Función con la lógica para procesar el fichero CSV (convertir a JSON y mandar a back)
  const _proccessFile = (lines) => {
    // Procesamos el contenido 
    const result = _checkContent(lines);
    const errors = result.errors;
    const goodLines = result.goodLines;

    let json = {};
    // Si no hay errores y tampoco líneas buenas
    if (errors.length === 0 && goodLines.length === 0) {
      errors.push(ERROR_MESSAGES.EMPTY_FILE());
    } else { // En otro caso
      // Creamos el json a partir del fichero CSV
      json = _createJSON(goodLines);
    }

    return { errors: errors, json: json }
  }

  // Función para crear el JSON
  const _createJSON = (goodLines) => {
    const jsonData = goodLines.map((line) => {
      const values = line.split(';');
      const obj = {};
      jsonVariableNames.forEach((header, index) => {
        const value = values[index];
        // Formateamos fecha para que no dé error
        if (['fecha_inicio', 'fecha_fin'].includes(header)) {
          obj[header] = _formatDate(value);
        }
        // Comprobamos que la cabecera sea mesX
        else if (mesRegex.test(header) && value === "") {
          obj[header] = null;
        } else {
          obj[header] = values[index];
        }
      });
      return obj;
    });
    return jsonData;
  }

  // Función para obtener la extensión del fichero
  const _getExtension = (filename) => {
    return filename.split('.').pop()
  }

  // Función para resetear el nombre y contenido del fichero
  const _resetFile = () => {
    // Resetear el estado después de subir el fichero
    setIsLoading(false);
    fileInputRef.current.value = '';
    setFileName(DEFAULT_TEXT);
    setFile(null);
  }

  // HTML
  return (
    <>
      <div className='header' style={{ display: 'flex', justifyContent: 'space-between', padding: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input type="file" id="seleccionarFichero" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} accept=".csv" />
          {/* <ButtonLeft htmlFor="seleccionarFichero"> */}
          <ButtonLeft
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            onMouseUp={(e) => e.currentTarget.blur()}
            disabled={isLoading}
          >
            Seleccionar Fichero
          </ButtonLeft>
          <span style={{
            margin: '5px 10px 0 10px',
            width: '170px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'inline-block'
          }}>
            {file ? `Fichero: ${fileName}` : fileName}
          </span>
          <ButtonCenter
            onClick={handleFileUpload}
            disabled={!file || isLoading}
          >
            Subir Fichero
          </ButtonCenter>
          <div style={{ position: 'relative', marginTop: '5px' }}>
            {isLoading && (
              <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)' }}>
                <Spinner />
              </div>
            )}
          </div>
        </div>
        <ButtonRight onClick={downloadEmptyCSV}>
          Descargar Plantilla CSV
        </ButtonRight>
      </div>
      <ObjectivesAdminCreationErrorsModal
        ref={modalRef}
        onAccept={_sendJson}
      />
    </>
  );
};

export default ObjectivesAdminCreationHeader;

// DISEÑOS
const ButtonLeft = styled(AntButton)`
  background: #008200;
  font-size: 16px;
  line-height: 1.5;
  color: white;
  margin-top: 5px;
  &:hover: {
    background: #008200;
    color: white;
  }
  margin-left: 40px;
`;

const ButtonCenter = styled(AntButton)`
  background: #008200;
  font-size: 16px;
  line-height: 1.5;
  color: white;
  margin-top: 5px;
  &:hover: {
    background: #008200;
    color: white;
  }
`

const ButtonRight = styled(AntButton)`
  background: #008200;
  font-size: 16px;
  line-height: 1.5;
  color: white;
  margin-top: 5px;
  &:hover: {
    background: #008200;
    color: white;
  }
  margin-right: 40px;
`


